import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Eye, EyeOff, Plus, Trash2, Lock, KeyRound, ChevronLeft, ChevronRight,
  Repeat, Download, Upload, Home, TrendingUp, TrendingDown, AlertTriangle, Sparkles,
} from 'lucide-react';
import { getModuleOutput } from '../../services/api';

/* ────────────────────────────────────────────────────────────────
   Storage

   v1 kept a flat income list plus budget rows whose `spent` figure
   was typed in by hand. Nobody keeps that up to date, so v2 stores
   dated entries and derives every total from them.
   ──────────────────────────────────────────────────────────────── */

const STORAGE_KEY = 'rdi_finance_v2';
const LEGACY_KEY = 'rdi_finance_v1';
const MAX_BACKFILL_MONTHS = 12;

const EMPTY = { version: 2, entries: [], budgets: {}, recurring: [] };

function migrate(legacy) {
  const entries = [];
  const budgets = {};
  const today = new Date().toISOString().slice(0, 10);

  (legacy.income || []).forEach((i, n) => {
    entries.push({
      id: `m-i-${n}-${Date.now()}`,
      date: i.date || today,
      kind: 'income',
      category: i.source || 'Income',
      note: '',
      amount: Number(i.amount) || 0,
    });
  });

  // A v1 category carried one lump "spent" number with no dates behind it.
  // It becomes a single entry so the figure survives rather than vanishing.
  (legacy.budget || []).forEach((b, n) => {
    if (b.category) budgets[b.category] = Number(b.budgeted) || 0;
    const spent = Number(b.spent) || 0;
    if (spent > 0) {
      entries.push({
        id: `m-b-${n}-${Date.now()}`,
        date: today,
        kind: 'expense',
        category: b.category || 'Other',
        note: 'Imported from previous version',
        amount: spent,
      });
    }
  });

  return { ...EMPTY, entries, budgets };
}

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return { ...EMPTY, ...p, budgets: p.budgets || {}, entries: p.entries || [], recurring: p.recurring || [] };
    }
  } catch (_) { /* fall through to migration */ }

  try {
    const old = localStorage.getItem(LEGACY_KEY);
    if (old) return migrate(JSON.parse(old));
  } catch (_) { /* ignore */ }

  return EMPTY;
}

/* ── Dates & money ───────────────────────────────────────────── */

const monthKey = (d) => String(d).slice(0, 7);
const thisMonth = () => new Date().toISOString().slice(0, 7);

function shiftMonth(key, by) {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** Clamps to the last day so a 31st recurring item still posts in February. */
function dateInMonth(key, day) {
  const [y, m] = key.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return `${key}-${String(Math.min(day || 1, last)).padStart(2, '0')}`;
}

function money(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

/* ── Presets, shaped around a luxury rental & concierge business ── */

const INCOME_PRESETS = ['Booking Revenue', 'Concierge Fee', 'Rental Income', 'Commission', 'Brand Partnership', 'Consulting'];
const EXPENSE_PRESETS = ['Property Costs', 'Vehicle & Fuel', 'Marketing', 'Travel', 'Dining & Client', 'Staff & Contractors', 'Insurance', 'Software', 'Personal'];

const inputCls =
  'w-full bg-transparent border border-white/10 px-3 py-2 text-sm text-white placeholder-gray-600 outline-none focus:border-white/30 tracking-wide';

/* ────────────────────────────────────────────────────────────────
   Panel
   ──────────────────────────────────────────────────────────────── */

export default function FinancePanel({ onLock, onChangePassword }) {
  const [state, setState] = useState(loadState);
  const [hidden, setHidden] = useState(false);
  const [month, setMonth] = useState(thisMonth);
  const [tab, setTab] = useState('ledger');

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) { /* quota */ }
  }, [state]);

  // Recurring items post themselves, so rent and insurance never have to be
  // re-typed. Matching on (recurringId, month) makes this safe to run on every
  // mount and in several tabs at once.
  useEffect(() => {
    setState((s) => {
      if (!s.recurring.length) return s;
      const have = new Set(s.entries.filter((e) => e.recurringId).map((e) => `${e.recurringId}|${monthKey(e.date)}`));
      const added = [];
      const now = thisMonth();
      for (let i = MAX_BACKFILL_MONTHS - 1; i >= 0; i--) {
        const key = shiftMonth(now, -i);
        for (const r of s.recurring) {
          if (r.endedOn && key > monthKey(r.endedOn)) continue;
          if (r.startedOn && key < monthKey(r.startedOn)) continue;
          if (have.has(`${r.id}|${key}`)) continue;
          added.push({
            id: `r-${r.id}-${key}`,
            date: dateInMonth(key, r.dayOfMonth),
            kind: r.kind,
            category: r.category,
            note: r.note || '',
            amount: Number(r.amount) || 0,
            recurringId: r.id,
          });
        }
      }
      return added.length ? { ...s, entries: [...s.entries, ...added] } : s;
    });
  }, [state.recurring]);

  const inMonth = useMemo(
    () => state.entries.filter((e) => monthKey(e.date) === month)
      .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
    [state.entries, month],
  );

  const totals = useMemo(() => {
    let income = 0, spent = 0;
    const byCategory = {};
    for (const e of inMonth) {
      const amt = Number(e.amount) || 0;
      if (e.kind === 'income') income += amt;
      else {
        spent += amt;
        byCategory[e.category] = (byCategory[e.category] || 0) + amt;
      }
    }
    const budgeted = Object.values(state.budgets).reduce((s, v) => s + (Number(v) || 0), 0);
    return { income, spent, net: income - spent, budgeted, byCategory };
  }, [inMonth, state.budgets]);

  const show = (n) => (hidden ? '••••' : money(n));

  function addEntry(entry) {
    setState((s) => ({ ...s, entries: [...s.entries, { id: `e-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...entry }] }));
  }

  function removeEntry(id) {
    setState((s) => ({ ...s, entries: s.entries.filter((e) => e.id !== id) }));
  }

  const TABS = [
    { key: 'ledger', label: 'Ledger' },
    { key: 'budget', label: 'Budget' },
    { key: 'recurring', label: 'Recurring' },
    { key: 'property', label: 'Property' },
  ];

  return (
    <div className="space-y-6">

      <div className="flex items-center justify-between border border-white/8 px-4 py-3">
        <div className="flex items-center gap-2">
          <Lock size={12} style={{ color: 'var(--gold)' }} />
          <p className="text-[9px] tracking-[0.2em] uppercase text-gray-500">Unlocked · Device only</p>
        </div>
        <div className="flex items-center gap-3">
          {onChangePassword && (
            <button onClick={onChangePassword}
              className="flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-gray-600 hover:text-white transition-colors">
              <KeyRound size={12} /> Password
            </button>
          )}
          <button onClick={() => setHidden((h) => !h)}
            className="flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase text-gray-600 hover:text-white transition-colors">
            {hidden ? <EyeOff size={12} /> : <Eye size={12} />}{hidden ? 'Show' : 'Hide'}
          </button>
          {onLock && (
            <button onClick={onLock}
              className="text-[9px] tracking-[0.15em] uppercase text-gray-600 hover:text-white transition-colors">
              Lock
            </button>
          )}
        </div>
      </div>

      <MonthBar month={month} onShift={(by) => setMonth((m) => shiftMonth(m, by))} />

      <div className="grid grid-cols-3 gap-2">
        <SummaryCard label="In" value={show(totals.income)} gold />
        <SummaryCard label="Out" value={show(totals.spent)} />
        <SummaryCard label="Net" value={show(totals.net)} tone={totals.net >= 0 ? 'pos' : 'neg'} />
      </div>

      <Insights state={state} month={month} totals={totals} hidden={hidden} />

      <div className="flex gap-4 border-b border-white/8 pb-3 overflow-x-auto scrollbar-none">
        {TABS.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className="text-[9px] tracking-[0.25em] uppercase transition-colors pb-1 -mb-[13px] border-b whitespace-nowrap"
            style={tab === key
              ? { color: 'var(--gold)', borderColor: 'var(--gold)' }
              : { color: '#555', borderColor: 'transparent' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'ledger' && (
        <Ledger entries={inMonth} month={month} show={show} onAdd={addEntry} onRemove={removeEntry} />
      )}

      {tab === 'budget' && (
        <Budget
          budgets={state.budgets}
          byCategory={totals.byCategory}
          show={show}
          onSet={(cat, amt) => setState((s) => ({ ...s, budgets: { ...s.budgets, [cat]: amt } }))}
          onRemove={(cat) => setState((s) => {
            const b = { ...s.budgets };
            delete b[cat];
            return { ...s, budgets: b };
          })}
        />
      )}

      {tab === 'recurring' && (
        <Recurring
          recurring={state.recurring}
          show={show}
          onAdd={(r) => setState((s) => ({ ...s, recurring: [...s.recurring, { id: `rc-${Date.now()}`, startedOn: `${thisMonth()}-01`, ...r }] }))}
          onRemove={(id) => setState((s) => ({
            ...s,
            recurring: s.recurring.filter((r) => r.id !== id),
            // Future auto-posted copies go too; past months stay as history.
            entries: s.entries.filter((e) => !(e.recurringId === id && monthKey(e.date) >= thisMonth())),
          }))}
        />
      )}

      {tab === 'property' && <PropertyImpact monthlyNet={totals.net} show={show} hidden={hidden} />}

      <Backup state={state} onRestore={setState} />
    </div>
  );
}

/* ── Month navigation ────────────────────────────────────────── */

function MonthBar({ month, onShift }) {
  const isNow = month === thisMonth();
  return (
    <div className="flex items-center justify-between">
      <button onClick={() => onShift(-1)} aria-label="Previous month"
        className="p-2 text-gray-600 hover:text-white transition-colors">
        <ChevronLeft size={16} />
      </button>
      <div className="text-center">
        <p className="text-[11px] tracking-[0.25em] uppercase">{monthLabel(month)}</p>
        {!isNow && <p className="text-[8px] tracking-[0.2em] uppercase text-gray-600 mt-0.5">Past month</p>}
      </div>
      <button onClick={() => onShift(1)} aria-label="Next month"
        className="p-2 text-gray-600 hover:text-white transition-colors">
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

function SummaryCard({ label, value, gold, tone }) {
  const color = gold ? 'var(--gold)' : tone === 'neg' ? '#f87171' : tone === 'pos' ? '#4ade80' : '#fff';
  return (
    <div className="border border-white/8 p-3 text-center">
      <p className="text-sm font-light truncate" style={{ color }}>{value}</p>
      <p className="text-[9px] tracking-[0.2em] uppercase text-gray-600 mt-1">{label}</p>
    </div>
  );
}

/* ── Insights, computed here so no figure ever leaves the device ── */

function Insights({ state, month, totals, hidden }) {
  const notes = useMemo(() => {
    const out = [];
    if (!totals.income && !totals.spent) return out;

    const prevKey = shiftMonth(month, -1);
    let prevIn = 0, prevOut = 0;
    for (const e of state.entries) {
      if (monthKey(e.date) !== prevKey) continue;
      if (e.kind === 'income') prevIn += Number(e.amount) || 0;
      else prevOut += Number(e.amount) || 0;
    }

    if (totals.income > 0) {
      const rate = Math.round((totals.net / totals.income) * 100);
      out.push({
        icon: rate >= 20 ? TrendingUp : TrendingDown,
        tone: rate >= 20 ? 'pos' : rate >= 0 ? 'warn' : 'neg',
        text: rate >= 0
          ? `Keeping ${rate}% of what came in.`
          : `Spending ${Math.abs(rate)}% more than came in.`,
      });
    }

    const over = Object.entries(state.budgets)
      .filter(([cat, amt]) => amt > 0 && (totals.byCategory[cat] || 0) > amt);
    if (over.length) {
      out.push({
        icon: AlertTriangle,
        tone: 'neg',
        text: over.length === 1
          ? `${over[0][0]} is over budget.`
          : `${over.length} categories over budget: ${over.map(([c]) => c).join(', ')}.`,
      });
    }

    const top = Object.entries(totals.byCategory).sort((a, b) => b[1] - a[1])[0];
    if (top && totals.spent > 0) {
      const share = Math.round((top[1] / totals.spent) * 100);
      if (share >= 30) {
        out.push({ icon: Sparkles, tone: 'warn', text: `${top[0]} is ${share}% of everything you spent.` });
      }
    }

    // Revenue in this business is lumpy, so a swing is worth flagging either way.
    if (prevIn > 0 && totals.income > 0) {
      const delta = Math.round(((totals.income - prevIn) / prevIn) * 100);
      if (Math.abs(delta) >= 20) {
        out.push({
          icon: delta > 0 ? TrendingUp : TrendingDown,
          tone: delta > 0 ? 'pos' : 'neg',
          text: `Income is ${Math.abs(delta)}% ${delta > 0 ? 'up on' : 'down on'} ${monthLabel(prevKey).split(' ')[0]}.`,
        });
      }
    }

    if (prevOut > 0 && totals.spent > 0) {
      const delta = Math.round(((totals.spent - prevOut) / prevOut) * 100);
      if (Math.abs(delta) >= 15) {
        out.push({
          icon: delta > 0 ? TrendingUp : TrendingDown,
          tone: delta > 0 ? 'warn' : 'pos',
          text: `Spending is ${Math.abs(delta)}% ${delta > 0 ? 'higher' : 'lower'} than ${monthLabel(prevKey).split(' ')[0]}.`,
        });
      }
    }

    // Mid-month, the honest read is the projection rather than the raw total.
    if (month === thisMonth() && totals.spent > 0) {
      const now = new Date();
      const day = now.getDate();
      const days = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      if (day >= 5 && day < days - 2) {
        const projected = Math.round((totals.spent / day) * days);
        out.push({ icon: Sparkles, tone: 'warn', text: `On pace for about ${money(projected)} out by month end.` });
      }
    }

    const fixed = state.recurring
      .filter((r) => r.kind === 'expense')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    if (fixed > 0 && totals.income > 0) {
      const share = Math.round((fixed / totals.income) * 100);
      out.push({ icon: Repeat, tone: share > 60 ? 'neg' : 'neutral', text: `Fixed monthly costs take ${share}% of income.` });
    }

    return out.slice(0, 4);
  }, [state, month, totals]);

  if (hidden || !notes.length) return null;

  const colorFor = (t) => (t === 'neg' ? '#f87171' : t === 'pos' ? '#4ade80' : t === 'warn' ? 'var(--gold)' : '#777');

  return (
    <div className="border-l-2 pl-4 py-1 space-y-2" style={{ borderColor: 'var(--gold-dim)' }}>
      {notes.map((n, i) => {
        const Icon = n.icon;
        return (
          <div key={i} className="flex items-start gap-2.5">
            <Icon size={12} className="mt-0.5 shrink-0" style={{ color: colorFor(n.tone) }} />
            <p className="text-[11px] text-gray-400 leading-relaxed">{n.text}</p>
          </div>
        );
      })}
    </div>
  );
}

/* ── Ledger ──────────────────────────────────────────────────── */

function Ledger({ entries, month, show, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('expense');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(() => dateInMonth(month, new Date().getDate()));

  useEffect(() => { setDate(dateInMonth(month, new Date().getDate())); }, [month]);

  const presets = kind === 'income' ? INCOME_PRESETS : EXPENSE_PRESETS;

  function submit(e) {
    e.preventDefault();
    const amt = Number(amount);
    if (!category.trim() || !amt || amt <= 0) return;
    onAdd({ date, kind, category: category.trim(), note: note.trim(), amount: amt });
    setAmount(''); setNote(''); setCategory('');
    setOpen(false);
  }

  const days = useMemo(() => {
    const g = new Map();
    for (const e of entries) {
      if (!g.has(e.date)) g.set(e.date, []);
      g.get(e.date).push(e);
    }
    return [...g.entries()];
  }, [entries]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </p>
        <button onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase hover:opacity-80"
          style={{ color: 'var(--gold)' }}>
          <Plus size={12} /> Add
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="border border-white/10 p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {['expense', 'income'].map((k) => (
              <button key={k} type="button" onClick={() => { setKind(k); setCategory(''); }}
                className="py-2 text-[9px] tracking-[0.2em] uppercase border transition-colors"
                style={kind === k
                  ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
                  : { borderColor: 'rgba(255,255,255,0.1)', color: '#5a5a5a' }}>
                {k === 'expense' ? 'Money Out' : 'Money In'}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {presets.map((p) => (
              <button key={p} type="button" onClick={() => setCategory(p)}
                className="px-2.5 py-1 text-[9px] tracking-[0.1em] uppercase border transition-colors"
                style={category === p
                  ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
                  : { borderColor: 'rgba(255,255,255,0.1)', color: '#5a5a5a' }}>
                {p}
              </button>
            ))}
          </div>

          <input value={category} onChange={(e) => setCategory(e.target.value)}
            placeholder="Category" className={inputCls} />
          <div className="flex gap-2">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01"
              placeholder="Amount" inputMode="decimal" className={inputCls + ' flex-1'} />
            <input value={date} onChange={(e) => setDate(e.target.value)} type="date"
              className={inputCls + ' flex-1'} />
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)" className={inputCls} />

          <button type="submit" className="w-full text-[9px] tracking-[0.25em] uppercase font-semibold py-2.5"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
            Save Entry
          </button>
        </form>
      )}

      {days.map(([day, list]) => (
        <div key={day}>
          <p className="text-[8px] tracking-[0.25em] uppercase text-gray-700 mb-1.5">
            {new Date(day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </p>
          {list.map((e) => (
            <div key={e.id} className="flex items-center justify-between py-2.5 border-b border-white/8 last:border-0">
              <div className="min-w-0 flex items-center gap-2">
                {e.recurringId && <Repeat size={10} className="text-gray-700 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-medium tracking-wide truncate">{e.category}</p>
                  {e.note && <p className="text-[10px] text-gray-600 truncate">{e.note}</p>}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-light"
                  style={{ color: e.kind === 'income' ? 'var(--gold)' : '#bbb' }}>
                  {e.kind === 'income' ? '+' : '−'}{show(e.amount)}
                </span>
                <button onClick={() => onRemove(e.id)} aria-label="Delete"
                  className="text-gray-700 hover:text-red-400 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {!entries.length && (
        <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 py-10 text-center leading-relaxed">
          Nothing logged for {monthLabel(month).split(' ')[0]}.<br />
          <span className="text-gray-700">Tap Add, or set up Recurring once.</span>
        </p>
      )}
    </div>
  );
}

/* ── Budget: every "spent" figure is derived from the ledger ──── */

function Budget({ budgets, byCategory, show, onSet, onRemove }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');

  const rows = useMemo(() => {
    const cats = new Set([...Object.keys(budgets), ...Object.keys(byCategory)]);
    return [...cats]
      .map((c) => ({ category: c, budgeted: Number(budgets[c]) || 0, spent: byCategory[c] || 0 }))
      .sort((a, b) => b.spent - a.spent);
  }, [budgets, byCategory]);

  function submit(e) {
    e.preventDefault();
    const amt = Number(amount);
    if (!category.trim() || !amt) return;
    onSet(category.trim(), amt);
    setCategory(''); setAmount('');
    setOpen(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">Spend vs budget</p>
        <button onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase hover:opacity-80"
          style={{ color: 'var(--gold)' }}>
          <Plus size={12} /> Set limit
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="border border-white/10 p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {EXPENSE_PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setCategory(p)}
                className="px-2.5 py-1 text-[9px] tracking-[0.1em] uppercase border transition-colors"
                style={category === p
                  ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
                  : { borderColor: 'rgba(255,255,255,0.1)', color: '#5a5a5a' }}>
                {p}
              </button>
            ))}
          </div>
          <input value={category} onChange={(e) => setCategory(e.target.value)}
            placeholder="Category" className={inputCls} />
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01"
            placeholder="Monthly limit" inputMode="decimal" className={inputCls} />
          <button type="submit" className="w-full text-[9px] tracking-[0.25em] uppercase font-semibold py-2.5"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
            Save Limit
          </button>
        </form>
      )}

      <div className="space-y-4">
        {rows.map((b) => {
          const pct = b.budgeted > 0 ? Math.min(100, (b.spent / b.budgeted) * 100) : 0;
          const over = b.budgeted > 0 && b.spent > b.budgeted;
          const left = b.budgeted - b.spent;
          return (
            <div key={b.category}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-medium tracking-wide">{b.category}</p>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-gray-400">
                    {show(b.spent)}
                    {b.budgeted > 0 && <span className="text-gray-600"> / {show(b.budgeted)}</span>}
                  </span>
                  {b.budgeted > 0 && (
                    <button onClick={() => onRemove(b.category)} aria-label="Remove limit"
                      className="text-gray-700 hover:text-red-400 transition-colors">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
              {b.budgeted > 0 ? (
                <>
                  <div className="h-1.5 w-full bg-white/8 overflow-hidden">
                    <div className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: over ? '#f87171' : 'var(--gold)' }} />
                  </div>
                  <p className="text-[9px] tracking-[0.15em] uppercase mt-1"
                    style={{ color: over ? '#f87171' : '#5a5a5a' }}>
                    {over ? `${show(Math.abs(left))} over` : `${show(left)} left`}
                  </p>
                </>
              ) : (
                <p className="text-[9px] tracking-[0.15em] uppercase text-gray-700">No limit set</p>
              )}
            </div>
          );
        })}
        {!rows.length && (
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 py-10 text-center">
            Log some spending and it appears here.
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Recurring ───────────────────────────────────────────────── */

function Recurring({ recurring, show, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('expense');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState('1');

  function submit(e) {
    e.preventDefault();
    const amt = Number(amount);
    if (!category.trim() || !amt || amt <= 0) return;
    onAdd({ kind, category: category.trim(), amount: amt, dayOfMonth: Number(day) || 1, note: 'Recurring' });
    setCategory(''); setAmount('');
    setOpen(false);
  }

  const monthlyOut = recurring.filter((r) => r.kind === 'expense').reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const monthlyIn = recurring.filter((r) => r.kind === 'income').reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">Posts itself every month</p>
        <button onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase hover:opacity-80"
          style={{ color: 'var(--gold)' }}>
          <Plus size={12} /> Add
        </button>
      </div>

      {recurring.length > 0 && (
        <p className="text-[10px] text-gray-500">
          {show(monthlyIn)} in · {show(monthlyOut)} out, every month without touching it.
        </p>
      )}

      {open && (
        <form onSubmit={submit} className="border border-white/10 p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {['expense', 'income'].map((k) => (
              <button key={k} type="button" onClick={() => { setKind(k); setCategory(''); }}
                className="py-2 text-[9px] tracking-[0.2em] uppercase border transition-colors"
                style={kind === k
                  ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
                  : { borderColor: 'rgba(255,255,255,0.1)', color: '#5a5a5a' }}>
                {k === 'expense' ? 'Money Out' : 'Money In'}
              </button>
            ))}
          </div>
          <input value={category} onChange={(e) => setCategory(e.target.value)}
            placeholder="What is it? (e.g. Car note)" className={inputCls} />
          <div className="flex gap-2">
            <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" step="0.01"
              placeholder="Amount" inputMode="decimal" className={inputCls + ' flex-1'} />
            <div className="flex-1">
              <select value={day} onChange={(e) => setDay(e.target.value)} className={inputCls}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d} className="bg-black">Day {d}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="w-full text-[9px] tracking-[0.25em] uppercase font-semibold py-2.5"
            style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
            Save Recurring
          </button>
        </form>
      )}

      <div>
        {recurring.map((r) => (
          <div key={r.id} className="flex items-center justify-between py-3 border-b border-white/8 last:border-0">
            <div className="min-w-0">
              <p className="text-sm font-medium tracking-wide truncate">{r.category}</p>
              <p className="text-[9px] tracking-[0.15em] uppercase text-gray-600 mt-0.5">
                Day {r.dayOfMonth} · {r.kind === 'income' ? 'in' : 'out'}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm font-light" style={{ color: r.kind === 'income' ? 'var(--gold)' : '#bbb' }}>
                {show(r.amount)}
              </span>
              <button onClick={() => onRemove(r.id)} aria-label="Delete"
                className="text-gray-700 hover:text-red-400 transition-colors">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        {!recurring.length && (
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 py-10 text-center leading-relaxed">
            Nothing recurring yet.<br />
            <span className="text-gray-700">Add rent, car, insurance once — it posts itself.</span>
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Property impact, driven by Module 07's live deals ───────── */

const LOAN_YEARS = 30;

/** Standard amortised payment. Falls back to straight division at 0%. */
function mortgagePayment(principal, aprPct, years = LOAN_YEARS) {
  const r = aprPct / 100 / 12;
  const n = years * 12;
  if (r <= 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function PropertyImpact({ monthlyNet, show, hidden }) {
  const [downPct, setDownPct] = useState(20);
  const [apr, setApr] = useState(7);
  const [picked, setPicked] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['module-output', '07'],
    queryFn: () => getModuleOutput('07'),
    staleTime: 1000 * 60 * 10,
  });

  const deals = useMemo(() => {
    const list = (data && data.deals) || [];
    return list.map((d) => {
      const down = d.price * (downPct / 100);
      const loan = d.price - down;
      const pi = mortgagePayment(loan, apr);
      // Taxes, insurance and upkeep, at a conventional 2%/yr of value.
      const carrying = (d.price * 0.02) / 12;
      const cashflow = Math.round((d.est_rent || 0) - pi - carrying);
      return { ...d, down: Math.round(down), payment: Math.round(pi + carrying), cashflow };
    }).sort((a, b) => b.cashflow - a.cashflow);
  }, [data, downPct, apr]);

  if (isLoading) {
    return <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10">Loading deals…</p>;
  }
  if (error || !deals.length) {
    return (
      <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10 leading-relaxed">
        No deals from Module 07 yet.<br />
        <span className="text-gray-700">They refresh each morning at 4:30.</span>
      </p>
    );
  }

  const sel = deals.find((d) => d.address === picked);

  return (
    <div className="space-y-4">
      <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">
        What tonight's deals would do to your month
      </p>

      <div className="grid grid-cols-2 gap-3 border border-white/8 p-3">
        <Slider label="Down payment" value={downPct} min={0} max={50} step={5}
          display={`${downPct}%`} onChange={setDownPct} />
        <Slider label="Interest rate" value={apr} min={3} max={12} step={0.25}
          display={`${apr}%`} onChange={setApr} />
      </div>

      <div className="space-y-2">
        {deals.map((d) => {
          const active = d.address === picked;
          const pos = d.cashflow >= 0;
          return (
            <button key={d.address} onClick={() => setPicked(active ? null : d.address)}
              className="w-full text-left border p-3 transition-colors"
              style={{ borderColor: active ? 'var(--gold)' : 'rgba(255,255,255,0.08)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium tracking-wide truncate">{d.address}</p>
                  <p className="text-[9px] tracking-[0.15em] uppercase text-gray-600 mt-0.5">
                    {money(d.price)} · {d.yield_pct}% yield
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-light" style={{ color: pos ? '#4ade80' : '#f87171' }}>
                    {pos ? '+' : '−'}{show(Math.abs(d.cashflow))}
                  </p>
                  <p className="text-[8px] tracking-[0.2em] uppercase text-gray-600">per month</p>
                </div>
              </div>

              {active && (
                <div className="mt-3 pt-3 border-t border-white/8 space-y-1.5">
                  <Row label="Rent (modelled)" value={show(d.est_rent)} />
                  <Row label="Mortgage + carrying" value={`−${hidden ? '••••' : money(d.payment)}`} />
                  <Row label="Cash needed up front" value={show(d.down)} />
                  <Row
                    label="Your monthly net becomes"
                    value={show(monthlyNet + d.cashflow)}
                    strong
                    tone={monthlyNet + d.cashflow >= 0 ? 'pos' : 'neg'}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-[9px] text-gray-700 leading-relaxed">
        Estimate only. Assumes a {LOAN_YEARS}-year loan and 2% of value a year for taxes,
        insurance and upkeep. Rent comes from RentCast's model, not a signed lease.
        {sel ? '' : ' Tap a property for the breakdown.'}
      </p>
    </div>
  );
}

function Slider({ label, value, min, max, step, display, onChange }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[8px] tracking-[0.2em] uppercase text-gray-600">{label}</label>
        <span className="text-[10px]" style={{ color: 'var(--gold)' }}>{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-yellow-600" style={{ accentColor: 'var(--gold)' }} />
    </div>
  );
}

function Row({ label, value, strong, tone }) {
  const color = tone === 'pos' ? '#4ade80' : tone === 'neg' ? '#f87171' : strong ? '#fff' : '#999';
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] tracking-[0.1em] uppercase text-gray-600">{label}</span>
      <span className={`text-[11px] ${strong ? 'font-medium' : 'font-light'}`} style={{ color }}>{value}</span>
    </div>
  );
}

/* ── Backup: the data lives in this browser, so let it out ───── */

function Backup({ state, onRestore }) {
  const fileRef = useRef(null);
  const [msg, setMsg] = useState('');
  const [pending, setPending] = useState(null);

  function exportJSON() {
    try {
      const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rdi-finance-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg('Downloaded.');
    } catch (_) {
      setMsg('Could not export on this device.');
    }
  }

  // Restoring overwrites everything on the device, so the file is parsed and
  // held for an explicit in-page confirmation rather than a native dialog,
  // which phones and installed PWAs can silently dismiss.
  function importJSON(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const p = JSON.parse(String(reader.result));
        if (!Array.isArray(p.entries)) throw new Error('not a finance backup');
        setMsg('');
        setPending({ data: p, count: p.entries.length, name: file.name });
      } catch (err) {
        setPending(null);
        setMsg(`Could not read that file: ${err.message}`);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function applyRestore() {
    if (!pending) return;
    const p = pending.data;
    onRestore({ ...EMPTY, ...p, budgets: p.budgets || {}, recurring: p.recurring || [] });
    setMsg(`Restored ${pending.count} entries.`);
    setPending(null);
  }

  return (
    <div className="pt-4 border-t border-white/8 space-y-2">
      <div className="flex items-center gap-5">
        <button onClick={exportJSON}
          className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-gray-600 hover:text-white transition-colors">
          <Download size={12} /> Back up
        </button>
        <button onClick={() => fileRef.current && fileRef.current.click()}
          className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-gray-600 hover:text-white transition-colors">
          <Upload size={12} /> Restore
        </button>
        <input ref={fileRef} type="file" accept="application/json,.json" onChange={importJSON} className="hidden" />
      </div>

      {pending && (
        <div className="border p-3 space-y-3" style={{ borderColor: 'var(--gold)' }}>
          <p className="text-[11px] text-gray-300 leading-relaxed">
            Replace everything on this device with <span className="text-white">{pending.name}</span>?
            It holds {pending.count} {pending.count === 1 ? 'entry' : 'entries'}. This cannot be undone.
          </p>
          <div className="flex gap-2">
            <button onClick={applyRestore}
              className="flex-1 text-[9px] tracking-[0.2em] uppercase font-semibold py-2.5"
              style={{ backgroundColor: 'var(--gold)', color: '#000' }}>
              Replace
            </button>
            <button onClick={() => setPending(null)}
              className="flex-1 text-[9px] tracking-[0.2em] uppercase py-2.5 border border-white/15 text-gray-400 hover:text-white transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}
      <p className="text-[9px] text-gray-700 leading-relaxed flex items-start gap-1.5">
        <Home size={10} className="mt-0.5 shrink-0" />
        Nothing here is sent anywhere. Clearing this browser's data erases it, so keep a backup.
      </p>
      {msg && <p className="text-[9px] tracking-[0.15em] uppercase" style={{ color: 'var(--gold)' }}>{msg}</p>}
    </div>
  );
}
